/**
 * Settings tabs (founder 2026-10-28): one tab per section, linkable as `/profile?tab=<id>`
 * like the admin page's `?tab=`. The bar reuses the V2 Segmented control (Radix Tabs) and is
 * `sticky top-0` inside <main>, the shell's scroll container, so it stays directly under the
 * top bar. On a narrow screen it scrolls sideways (data-allow-scroll) instead of wrapping.
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Segmented } from '../ui/segmented';

export type SettingsTab =
  | 'profile'
  | 'headings'
  | 'price-notes'
  | 'scope'
  | 'generation'
  | 'appearance'
  | 'plan'
  | 'referrals'
  | 'session';

export const SETTINGS_TABS: Array<{ id: SettingsTab; label: string }> = [
  { id: 'profile', label: 'Profile' },
  { id: 'headings', label: 'Estimate headings' },
  { id: 'price-notes', label: 'Price notes' },
  { id: 'scope', label: 'Scope structure' },
  { id: 'generation', label: 'Generation defaults' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'plan', label: 'Plan and credits' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'session', label: 'Session' },
];

/** The tab named by `?tab=`; anything unknown (or a tab not offered) is Profile. */
export function useSettingsTab(offered: (_id: SettingsTab) => boolean) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const known = SETTINGS_TABS.find((t) => t.id === raw)?.id;
  const tab: SettingsTab = known && offered(known) ? known : 'profile';
  const setTab = (next: SettingsTab) =>
    setParams(
      (p) => {
        const out = new URLSearchParams(p);
        out.set('tab', next);
        return out;
      },
      { replace: true },
    );
  return [tab, setTab] as const;
}

export function SettingsTabBar({
  tab,
  onChange,
  offered,
}: {
  tab: SettingsTab;
  onChange: (_t: SettingsTab) => void;
  offered: (_id: SettingsTab) => boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);

  // A tab opened by link on a phone may sit past the right edge: bring it into view.
  useEffect(() => {
    const el = scroller.current?.querySelector<HTMLElement>('[data-state="active"]');
    if (el && scroller.current) {
      const s = scroller.current;
      if (el.offsetLeft < s.scrollLeft || el.offsetLeft + el.offsetWidth > s.scrollLeft + s.clientWidth) {
        s.scrollLeft = el.offsetLeft - 16;
      }
    }
  }, [tab]);

  return (
    <div
      className="sticky top-0 z-20 bg-[var(--bg-0)] border-b border-[var(--bd-subtle)]"
      data-testid="settings-tabbar"
    >
      <div
        ref={scroller}
        className="relative mx-auto max-w-[1280px] px-6 py-[10px] overflow-x-auto"
        data-allow-scroll
      >
        <Segmented<SettingsTab>
          value={tab}
          onValueChange={onChange}
          className="w-max [&>*]:whitespace-nowrap"
          options={SETTINGS_TABS.filter((t) => offered(t.id)).map((t) => ({
            value: t.id,
            label: t.label,
          }))}
        />
      </div>
    </div>
  );
}
